// Desktop Chromium (Windows/Linux) reports HEVC as supported through
// MediaSource.isTypeSupported even with no hardware decoder, so hls.js keeps the
// HEVC rungs, the ABR climbs into one, and software decode stutters on every
// ~2s CMAF segment. MediaCapabilities.decodingInfo tells them apart: it reports
// `powerEfficient` (hardware) vs software fallback. When HEVC is not smoothly
// hardware-decodable, we drop those rungs so the ABR stays on the H.264 ones
// the v8 ladder always carries.

export interface CodecLevel {
	videoCodec?: string;
	width?: number;
	height?: number;
	bitrate?: number;
}

export type DecodingInfoFn = (
	config: MediaDecodingConfiguration
) => Promise<MediaCapabilitiesDecodingInfo>;

/**
 * Apple WebKit: every iOS browser and in-app webview (Instagram included), iPadOS
 * in desktop mode, and Safari on macOS. These devices decode HEVC in hardware,
 * but decodingInfo for media-source answers as if they could not,
 * so the HEVC rungs were stripped. Chrome and Firefox on macOS answer
 * correctly and keep the check.
 */
export function isAppleWebKit(userAgent: string, maxTouchPoints = 0): boolean {
	if (/iPhone|iPad|iPod/.test(userAgent)) return true;
	const isMac = /Macintosh/.test(userAgent);
	if (isMac && maxTouchPoints > 1) return true;
	return (
		isMac &&
		/AppleWebKit/.test(userAgent) &&
		!/Chrome|Chromium|CriOS|Edg|Firefox|FxiOS|OPR/.test(userAgent)
	);
}

/**
 * The decodingInfo the HEVC gating should trust, or null to skip it (keep every
 * rung, treat hardware as available), which is what Apple WebKit needs.
 */
export function hevcDecodingInfo(): DecodingInfoFn | null {
	if (typeof navigator === 'undefined') return null;
	if (isAppleWebKit(navigator.userAgent, navigator.maxTouchPoints)) return null;
	const capabilities = navigator.mediaCapabilities;
	return capabilities?.decodingInfo
		? capabilities.decodingInfo.bind(capabilities)
		: null;
}

const HEVC_CODEC = /^(hvc1|hev1)\b/i;

export function isHevcLevel(level: CodecLevel): boolean {
	return !!level.videoCodec && HEVC_CODEC.test(level.videoCodec);
}

/**
 * Indices of the HEVC levels the device cannot decode smoothly in hardware, so
 * the caller can remove them. Guarantees:
 * - H.264 levels are never touched.
 * - Nothing is removed when the API is missing or throws (no regression on
 *   uncertainty — a working HEVC path must not be cut on a maybe).
 * - Never removes every level (a stuttering HEVC still beats a dead player), and
 *   only acts when at least one non-HEVC rung remains to fall back to.
 */
export async function hevcLevelsToRemove(
	levels: ReadonlyArray<CodecLevel>,
	decodingInfo: DecodingInfoFn | undefined | null
): Promise<number[]> {
	if (!decodingInfo) return [];
	const hevc = levels
		.map((level, index) => ({ level, index }))
		.filter(entry => isHevcLevel(entry.level));
	if (hevc.length === 0) return [];
	// No H.264 rung to fall back to — leave the ladder alone.
	if (levels.length - hevc.length === 0) return [];

	// All rungs probed at once: the window between MANIFEST_PARSED and the
	// removal is exactly when a video switch can invalidate the result, so it
	// should be one round trip, not one per rung.
	const infos = await Promise.all(
		hevc.map(
			async ({ level }): Promise<MediaCapabilitiesDecodingInfo | undefined> => {
				try {
					return await decodingInfo({
						type: 'media-source',
						video: {
							contentType: `video/mp4; codecs="${level.videoCodec}"`,
							width: level.width ?? 1920,
							height: level.height ?? 1080,
							bitrate: level.bitrate ?? 2_000_000,
							framerate: 30
						}
					});
				} catch {
					// Fail-open on uncertainty: API missing or throwing shouldn't break HEVC
					return undefined;
				}
			}
		)
	);
	const remove: number[] = [];
	hevc.forEach(({ index }, i) => {
		const info = infos[i];
		// Keep on uncertainty; remove only when the platform confirms it can't
		// decode this rung smoothly in hardware.
		if (info && (!info.supported || !info.smooth || !info.powerEfficient)) {
			remove.push(index);
		}
	});

	if (remove.length >= levels.length) return [];
	return remove;
}

/** What survives a ladder mutation: hls.js never rewrites these on a rung. */
export function levelIdentity(level: CodecLevel): string {
	return `${level.videoCodec ?? ''}|${level.height ?? ''}|${level.bitrate ?? ''}`;
}

/**
 * Maps indices computed against a pre-await snapshot onto the live ladder.
 * hls.js can drop a rung on its own within the same load (codec or key error),
 * so a snapshot index may now name a different rung. Targets are matched by
 * identity; ones already gone are skipped; and the result is never the whole
 * live ladder, so a caller cannot strip everything whatever moved underneath.
 */
export function resolveLevelIndices(
	snapshot: ReadonlyArray<CodecLevel>,
	indices: ReadonlyArray<number>,
	live: ReadonlyArray<CodecLevel>
): number[] {
	const targets = new Set(
		indices.flatMap(i => {
			const level = snapshot[i];
			return level ? [levelIdentity(level)] : [];
		})
	);
	const resolved = live
		.map((level, i) => (targets.has(levelIdentity(level)) ? i : -1))
		.filter(i => i >= 0);
	return resolved.length >= live.length ? [] : resolved;
}

/**
 * Returns whether the ladder contains HEVC rungs, indicating hardware HEVC
 * support survived the gating phase.
 */
export function hasHevcLevels(levels: ReadonlyArray<CodecLevel>): boolean {
	return levels.some(level => isHevcLevel(level));
}

/**
 * Every HEVC rung, for when the device already failed to decode HEVC. Empty when
 * no H.264 rung would remain: a ladder with nothing left to play is worse than a
 * rung that might still decode.
 */
export function hevcFallbackIndices(
	levels: ReadonlyArray<CodecLevel>
): number[] {
	const hevc = levels
		.map((level, index) => (isHevcLevel(level) ? index : -1))
		.filter(index => index >= 0);
	if (hevc.length === 0 || hevc.length === levels.length) return [];
	return hevc;
}

export interface DecodeProgress {
	readyState: number;
	/** Seconds buffered ahead of currentTime. */
	bufferedAhead: number;
	/** Frames decoded so far; null when getVideoPlaybackQuality is missing. */
	decodedFrames: number | null;
}

// HTMLMediaElement.HAVE_CURRENT_DATA, spelled out so this stays usable in tests.
const HAVE_CURRENT_DATA = 2;

/**
 * The hang that raises no error: segments arrived but not a single frame came out.
 * Some Android decoders (Motorola, Samsung, in-app webviews) accept HEVC on
 * MediaCapabilities and then produce nothing, so the viewer waits and leaves.
 */
export function isDecodeStall(progress: DecodeProgress): boolean {
	return (
		progress.readyState < HAVE_CURRENT_DATA &&
		progress.bufferedAhead >= 1 &&
		(progress.decodedFrames ?? 0) === 0
	);
}

interface BufferedRanges {
	readonly length: number;
	start(index: number): number;
	end(index: number): number;
}

/** Seconds buffered ahead of `currentTime` in the range that contains it. */
export function bufferedAhead(
	ranges: BufferedRanges,
	currentTime: number
): number {
	for (let i = 0; i < ranges.length; i++) {
		if (ranges.start(i) <= currentTime && currentTime <= ranges.end(i)) {
			return ranges.end(i) - currentTime;
		}
	}
	return 0;
}

/**
 * Frames decoded since `baseline`. The counter belongs to the element, which the
 * feed reuses across videos; a counter below the baseline was reset by the browser.
 */
export function framesDecodedSince(
	total: number | null,
	baseline: number
): number | null {
	if (total === null) return null;
	return total >= baseline ? total - baseline : total;
}

// The phone screen gains nothing past 1080p, and the 2160x3840 HEVC rung of the
// 29/09 re-encode is the one a phone decoder is most likely to refuse.
const MAX_SHORT_SIDE = 1080;

/** Rungs larger than 1080p on the short side; never the whole ladder. */
export function oversizedLevelIndices(
	levels: ReadonlyArray<CodecLevel>,
	maxShortSide = MAX_SHORT_SIDE
): number[] {
	const oversized = levels
		.map((level, index) =>
			level.width &&
			level.height &&
			Math.min(level.width, level.height) > maxShortSide
				? index
				: -1
		)
		.filter(index => index >= 0);
	return oversized.length >= levels.length ? [] : oversized;
}

export type HevcFallbackReason = 'media_error' | 'element_decode' | 'stall';

// A device that failed HEVC once fails it on every video of the session (48% of
// same-format plays after a failure on Android in Sep/2026), so the fallback sticks.
// A decode error is the device saying no; a stall only suggests it, so it has to
// happen twice before the session gives up on HEVC.
const HEVC_FAILED_KEY = 'pilar:hevc-decode-failed';
const STALLS_BEFORE_STICKY = 2;
let hevcFailedInMemory = false;
let storageRead = false;
let stallCount = 0;

export function markHevcDecodeFailed(reason: HevcFallbackReason): void {
	if (reason === 'stall' && ++stallCount < STALLS_BEFORE_STICKY) return;
	hevcFailedInMemory = true;
	storageRead = true;
	try {
		sessionStorage.setItem(HEVC_FAILED_KEY, '1');
	} catch {
		// Private mode or SSR: the in-memory flag still covers this page's lifetime.
	}
}

// Called on every canvas frame, so storage is read once and memory answers after.
export function hevcDecodeFailedThisSession(): boolean {
	if (!storageRead) {
		storageRead = true;
		try {
			hevcFailedInMemory ||= sessionStorage.getItem(HEVC_FAILED_KEY) === '1';
		} catch {
			// Storage unavailable: nothing was remembered.
		}
	}
	return hevcFailedInMemory;
}

/** Test hook: forgets the session failure. */
export function resetHevcDecodeFailure(): void {
	hevcFailedInMemory = false;
	storageRead = false;
	stallCount = 0;
	try {
		sessionStorage.removeItem(HEVC_FAILED_KEY);
	} catch {
		// Storage unavailable.
	}
}

/**
 * Pre-checks if the device can hardware-decode a representative HEVC config
 * (1080p30 2mbps hvc1) before the Hls instance is created. This determines
 * whether to set `videoPreference: { videoCodec: 'hvc1' }` in the constructor,
 * guiding initial selection toward HEVC per hls.js v1.7.0 API.md:
 * "videoPreference limits initial level selection when a baseline variant exists."
 *
 * Returns true (HW-OK) when MediaCapabilities confirms supported+smooth+powerEfficient,
 * false otherwise. Fail-open on API absence or throw: uncertainty should not break
 * an otherwise working HEVC path, so the caller may still attempt HEVC and rely on
 * the post-parse removeLevel gating to strip unplayable rungs.
 *
 * @see https://github.com/video-dev/hls.js/blob/v1.7.0/docs/API.md#videopreference
 */
export async function canHardwareDecodeHevc(
	decodingInfo: DecodingInfoFn | undefined | null
): Promise<boolean> {
	if (!decodingInfo) return true;
	try {
		const info = await decodingInfo({
			type: 'media-source',
			video: {
				contentType: 'video/mp4; codecs="hvc1.1.6.L120.90"',
				width: 1920,
				height: 1080,
				bitrate: 2_000_000,
				framerate: 30
			}
		});
		return info.supported && info.smooth && info.powerEfficient;
	} catch {
		return true;
	}
}
